import { useCallbackRef } from "@hooks/useCallbackRef"
import { useDrag } from "@use-gesture/react"
import { tv, VariantProps } from "@utils/tw"
import { getScrollSnapPositions } from "@zag-js/scroll-snap"
import { animate, motion, useMotionValue, useMotionValueEvent, useTransform } from "motion/react"
import { ComponentProps, PropsWithChildren, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react"

type BaseProps = Pick<VariantProps<typeof createStyles>, "fadeSize"> & {
  scrollerRef?: React.RefObject<SnapScroller>
  onCanScrollChange?: (value: { left: boolean; right: boolean }) => void
  onSlideChange?: (index: number) => void
  children: ReactNode
  className?: string
  speed?: "fast" | "slow"
}

export type SnapScroller = {
  go(direction: -1 | 1): void
}

type Props = BaseProps & Omit<PropsWithChildren<ComponentProps<"div">>, keyof BaseProps | "ref">

export function SnapScroller({
  fadeSize,
  className,
  onCanScrollChange,
  onSlideChange,
  scrollerRef,
  speed = "fast",
  ...props
}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const animatingToSlide = useRef(false)

  useImperativeHandle<SnapScroller, SnapScroller>(
    scrollerRef,
    () => ({
      go(dir) {
        if (!ref.current) return
        const { index, value } = getClosestSnap()
        const nextIndex = index + dir
        const target = snaps.current[nextIndex]
        ref.current.style.scrollSnapType = "none"
        animatingToSlide.current = true
        animate(
          dragPos,
          target,
          speed === "fast"
            ? {
                type: "spring",
                stiffness: 600,
                damping: 40,
              }
            : {
                type: "spring",
                stiffness: 200,
                damping: 30,
              },
        ).then(() => {
          animatingToSlide.current = false
          onSlideChange?.(nextIndex)
        })
      },
    }),
    [],
  )

  // Maintain a list of snap positions, updated on resize
  const snaps = useRef<number[]>([])
  const updateSnaps = () => {
    if (ref.current) {
      snaps.current = getScrollSnapPositions(ref.current).x
    }
    return snaps.current
  }
  const getClosestSnap = (value = ref.current?.scrollLeft ?? 0) => {
    const closestValue = [...snaps.current].sort((a, b) => Math.abs(a - value) - Math.abs(b - value))[0]
    const index = snaps.current.indexOf(closestValue)
    return { index, value: closestValue }
  }
  const updateCanScroll = useCallbackRef(() => {
    const { index } = getClosestSnap()
    const left = index > 0
    const right = index < snaps.current.length - 1
    setCanScrollLeft(left)
    setCanScrollRight(right)
    onCanScrollChange?.({
      left,
      right,
    })
    if (!animatingToSlide.current) {
      onSlideChange?.(index)
    }
  })

  // Update snaps on resize
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      updateSnaps()
      el.scrollLeft = getClosestSnap().value
      updateCanScroll()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Drag position, for draggign the scroller with a mouse
  const dragPos = useMotionValue(0)
  useMotionValueEvent(dragPos, "change", (v) => {
    ref.current?.scrollTo({ left: v, behavior: "instant" })
  })
  useMotionValueEvent(dragPos, "animationComplete", () => {
    if (ref.current) ref.current.style.scrollSnapType = ""
    updateSnaps()
  })

  // Get the overscroll amount, for rubberbanding. We fake this with transforms.
  const overscroll = useTransform(dragPos, (v) => {
    const maxScroll = Math.max(...snaps.current)
    if (v < 0) {
      return -v + "px"
    } else if (v > maxScroll) {
      return -(v - maxScroll) + "px"
    }
    return 0
  })

  // Drag gesture
  useDrag(
    ({ offset: [x], velocity: [vx], direction: [dir], swipe, delta: [dx], last, first, down, event, cancel }) => {
      if (event.type.match(/touch|pointer/)) {
        // cancel()
        return
      }
      if (first) {
        // First drag event of this gesture. Disable scroll snapping and stop any ongoing animations.
        dragPos.stop()
        if (ref.current) ref.current.style.scrollSnapType = "none"
      } else if (last || !down) {
        // On release, animate to the closest snap point
        const dragTarget = -x - Math.pow(vx, 1.2) * dir * 100
        let target = getClosestSnap(dragTarget).value
        animate(dragPos, target, {
          type: "spring",
          stiffness: 600,
          damping: 40,
        })
      } else {
        // Update the drag position
        dragPos.stop()
        dragPos.set(-x)
      }
    },
    {
      target: ref,
      axis: "x",
      preventDefault: false,
      // preventScrollAxis: "x",
      // preventScroll: false,
      rubberband: true,
      filterTaps: true,
      enabled: !env.admin,
      from: () => [-ref.current?.scrollLeft! || 0, 0],
      bounds: () => ({ left: -Math.max(...snaps.current), right: 0 }),
      pointer: { mouse: true, buttons: 1, touch: false },
      eventOptions: {
        passive: false,
      },
    },
  )

  const styles = createStyles({
    canScrollLeft,
    canScrollRight,
    fadeSize,
    className,
  })

  return (
    <motion.div
      {...(props as any)}
      className={styles}
      ref={ref}
      style={{ "--overscroll": overscroll } as any}
      onScroll={updateCanScroll}
      onWheel={() => {
        // On wheel, cancel any ongoing animations and reset scroll snapping
        dragPos.stop()
        if (ref.current) ref.current.style.scrollSnapType = ""
      }}
    >
      {props.children}
    </motion.div>
  )
}

SnapScroller.displayName = "SnapScroller"

const createStyles = tv({
  base: "overflow-x-auto scrollbars-hidden scroll-mask overscroll-x-contain snap-mandatory snap-x isolate [&>*]:translate-x-[--overscroll]",
  variants: {
    canScrollLeft: { true: "scroll-mask-start" },
    canScrollRight: { true: "scroll-mask-end" },
    fadeSize: {
      sm: "scroll-mask-4",
      md: "scroll-mask-[3vw] xl:scroll-mask-[80px]",
      lg: "scroll-mask-[5vw] xl:scroll-mask-[150px]",
    },
  },
  defaultVariants: {},
})
